import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ColumnMode, DatatableComponent, SelectionType, SortType } from '@swimlane/ngx-datatable';

import { FacebookService } from 'ngx-facebook';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, mergeMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { NameValue, UserProfile } from '../../core/models';
import { EventService, ToastService, InviteService, ValidationService, DynamicScriptLoaderService } from '../../core/services';
import { ConfirmComponent } from '../../shared/components';
import { EMAIL_REGEXP, GiftShareValidators, NAME_REGEXP } from '../../shared/validators';
import { ViewportScroller } from '@angular/common';

declare var gapi: any;
declare var XLSX: any;
declare var WL: any;

export const CURRENT_EVENT_KEY: string = 'currentEventCode';

@Component({
    selector: 'gs-invite',
    templateUrl: './invite.component.html',
    standalone: false
})
export class InviteComponent implements OnInit, OnDestroy {
  @ViewChild('fileImportInput') fileImportInput: any;
  @ViewChild('fileImportInputExcel') fileImportInputExcel: any;
  @ViewChild(DatatableComponent) contactsTable: DatatableComponent;

  ColumnMode = ColumnMode;
  SortType = SortType;
  SelectionType = SelectionType;

  importSources = {
    CSV: 0,
    EXCEL: 1,
    FACEBOOK: 2,
    GOOGLE: 3,
    OUTLOOK: 4
  };

  selectedImportSource: number = this.importSources.CSV;

  tabs = {
    INVITED: 0,
    IMPORT: 1
  };

  selectedTab: number = this.tabs.INVITED;

  steps = {
    FILE: 0,
    MAPPING: 1,
    CONTACTS: 2
  };

  selectedStep: number = this.steps.FILE;

  eventCode: string;
  contributions = [];
  private user: UserProfile;
  private importedRawData = [];
  importedContacts = [];
  selectedContacts = [];
  headers: NameValue[] = [];

  filteredContacts = [];
  contacts = [];
  page: number = 1;
  itemsPerPage: number = 10;

  editedItem: any;

  scriptsLoaded: boolean = false;

  form: FormGroup = this.formBuilder.group({
    name: ['', [Validators.required,
    Validators.maxLength(100), Validators.pattern(NAME_REGEXP)]],
    email: ['', [Validators.required, Validators.maxLength(256),
    GiftShareValidators.email]]
  });

  formMapping: FormGroup = this.formBuilder.group({
    columnName: [null, [Validators.required]],
    columnEmail: [null, [Validators.required]]
  });

  formFilter: FormGroup = this.formBuilder.group({
    status: ['all'],
    keyword: [null]
  });

  private editContactModal: NgbModalRef;
  private subscriptions: Subscription[] = [];

  get canAddContacs(): boolean {
    return !!this.selectedContacts?.length;
  }

  get nextLabel(): string {
    return this.selectedStep == this.steps.CONTACTS ? 'Importa' : 'Inainte';
  }

  get backLabel(): string {
    return this.selectedStep == this.steps.MAPPING ? 'Anuleaza' : 'Inapoi';
  }

  constructor(
    private inviteService: InviteService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private eventService: EventService,
    private http: HttpClient,
    private modalService: NgbModal,
    private facebookService: FacebookService,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private store: Store,
    private formBuilder: FormBuilder,
    public vs: ValidationService,
    private viewportScroller: ViewportScroller
  ) {
    this.subscriptions.push(
      this.dynamicScriptLoaderService
        .loadMultipleScriptsByName('jspdf', 'xlsx')
        .pipe(mergeMap(() => this.dynamicScriptLoaderService
          .loadScriptByName('jspdf.autotable')))
        .subscribe(() => {
          this.scriptsLoaded = true;
        })
    );
  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        })
    );

    this.eventCode = this.route.snapshot.paramMap.get('eventCode');

    this.getInvitedContacts();

    this.subscriptions.push(
      this.formFilter.valueChanges
        .pipe(
          debounceTime(100),
          distinctUntilChanged()
        )
        .subscribe(x => {
          this.filterContacts(
            !x.keyword || x.keyword.length < 3 ? '' : x.keyword,
            x.status
          );
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onTabSelected(id: number) {
    this.selectedTab = id;
    this.scroll();
  }

  onStepSelected(id: number) {
    this.selectedStep = id;
  }

  onEditClick(contact: any, template: TemplateRef<any>) {
    this.editedItem = contact;

    this.form.patchValue({
      ...contact
    });

    this.editContactModal = this.modalService.open(template);
  }

  onDeleteClick(contact: any) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Sterge contact';
    modalRef.componentInstance.description = `Datele asociate contactului vor fi permanent sterse si nu pot fi recuperate.`;
    modalRef.componentInstance.acceptLabel = 'Sterge';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Anuleaza';

    this.subscriptions.push(
      modalRef.componentInstance.done
        .subscribe(result => {
          if (result === 'accept') {
            this.deleteContact(contact.id);
          }
        })
    );
  }

  filterContacts(keyword: string = null, status: string = 'all') {
    if (!this.contacts) {
      return;
    }

    this.filteredContacts = this.contacts.filter(x =>
      (!keyword || x.name && x.name.toUpperCase().indexOf(keyword.toUpperCase()) >= 0 || x.email && x.email.toUpperCase().indexOf(keyword.toUpperCase()) >= 0) &&
      (
        status == 'all' ||
        status == 'unsent' && !x.invitedDate ||
        status == 'sent' && x.invitedDate ||
        status == 'contributed' && this.contributions.some(y => x.email.toUpperCase() === y.email.toUpperCase()) ||
        status == 'not-contributed' && !this.contributions.some(y => x.email.toUpperCase() === y.email.toUpperCase())
      )
    );

    this.resetPage();
  }

  onPageChanged() {
    this.scroll();
  }

  private resetPage() {
    this.page = 1;
  }

  private scroll() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  onNextClick() {
    switch (this.selectedStep) {
      case this.steps.FILE: {
        break;
      }
      case this.steps.MAPPING: {
        this.processRawContacts();
        break;
      }
      case this.steps.CONTACTS: {
        this.submitContacts();
        break;
      }
    }
  }

  onBackClick() {
    switch (this.selectedStep) {
      case this.steps.FILE: {
        break;
      }
      case this.steps.MAPPING: {
        this.cancelImport();
        break;
      }
      case this.steps.CONTACTS: {
        this.onStepSelected(this.steps.MAPPING);
        break;
      }
    }
  }

  // Keep selected in sync issue: https://github.com/swimlane/ngx-datatable/issues/1530
  onSelect({ selected }) {
    this.selectedContacts.splice(0, this.selectedContacts.length);
    this.selectedContacts.push(...selected);
  }

  /**
   * Invite Facebook friends
   * @memberof InviteComponent
   */
  onFacebookInviteFriendsClick() {
    this.resetData();
    this.selectedImportSource = this.importSources.FACEBOOK;

    const url: string = environment.frontEndUrl + '/eveniment/' + this.eventCode;

    this.facebookService.ui({
      method: 'send',
      display: 'popup',
      link: url
    });
  }

  onImportOutlookContactsClick() {
    this.resetData();
    this.selectedImportSource = this.importSources.OUTLOOK;

    WL.login({
      scope: ['wl.signin', 'wl.basic', 'wl.postal_addresses', 'wl.phone_numbers', 'wl.contacts_emails', 'wl.emails']
    }).then((response: any) => {

      const token = response.session.access_token;
      const httpOptions = {
        headers: new HttpHeaders().set('Authorization', 'Bearer ' + token).set('Content-Type', 'application/json')
      };

      this.http.get<any>('https://outlook.office.com/api/v2.0/me/contacts?$select=EmailAddresses,GivenName,Surname&$top=1000', httpOptions)
        .subscribe((response: any) => {
          for (const item of response.value) {
            if (item.EmailAddresses) {
              for (const emailAddress of item.EmailAddresses) {

                const name: string = `${item.GivenName} ${item.Surname}`;
                const email: string = emailAddress.Address;

                if (!this.isValidItem(name, email)) {
                  console.log(`Datele ${name}, ${email} sunt invalide.`);
                  continue;
                }

                this.importedContacts.push({
                  name: name,
                  email: email,
                  state: true
                });
              }
            }
          }

          this.onStepSelected(this.steps.CONTACTS);

          WL.logout();
        });
    });
  }

  onImportGoogleContactsClick() {
    this.resetData();
    this.selectedImportSource = this.importSources.GOOGLE;

    // this.onStepSelected(this.steps.CONTACTS);

    gapi.client.setApiKey(environment.googleApiKey);
    gapi.auth.authorize({
      client_id: environment.googleClientId,
      scope: 'https://www.google.com/m8/feeds',
      immediate: false
    }).then((data: any) => {
      this.subscriptions.push(
        this.http.get<any>('https://people.googleapis.com/v1/people/me/connections?pageSize=2000&personFields=names,emailAddresses&access_token=' + data.access_token)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            })
          )
          .subscribe((response: any) => {

            for (let i = 0; i < response.connections.length; i++) {
              const name: string = response.connections[i].hasOwnProperty('names') ? response.connections[i].names[0].displayName.trim() : '';
              const email: string = response.connections[i].hasOwnProperty('emailAddresses') ? response.connections[i].emailAddresses[0].value.trim() : '';

              if (!this.isValidItem(name, email)) {
                console.log(`Datele ${name}, ${email} sunt invalide.`);
                continue;
              }

              this.importedContacts.push({
                name: name,
                email: email,
                state: true
              });
            }

            this.onStepSelected(this.steps.CONTACTS);

          })
      );
    });
  }

  private processRawContacts() {
    if (!this.formMapping.valid) {
      this.vs.triggerValidation(this.formMapping);
      return;
    }

    this.importedContacts = [];

    for (let i = 0; i < this.importedRawData.length; i++) {

      const name: string = this.importedRawData[i].hasOwnProperty(this.formMapping.value.columnName) ? this.importedRawData[i][this.formMapping.value.columnName].trim() : '';
      const email: string = this.importedRawData[i].hasOwnProperty(this.formMapping.value.columnEmail) ? this.importedRawData[i][this.formMapping.value.columnEmail].trim() : '';

      if (!this.isValidItem(name, email)) {
        this.toastService.show('Eroare', `Datele de la linia ${i + 2} sunt invalide.`, 'error');
        this.onStepSelected(this.steps.FILE);
        return;
      }

      this.importedContacts.push({
        name: name,
        email: email
      });
    }

    this.onStepSelected(this.steps.CONTACTS);
    // setTimeout(() => {
    //   this.contactsTable.onHeaderSelect(true);
    // }, 100);
    this.selectedContacts = [...this.importedContacts];

  }

  private submitContacts() {
    const currentEventCode = localStorage.getItem(CURRENT_EVENT_KEY);
    if (!currentEventCode) {
      this.toastService.show('Eroare', 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
    }

    if (this.selectedContacts.length == 0) {
      this.toastService.show('Eroare', 'Selecteaza cel putin un contact.', 'error');
      return;
    }

    const model = {
      eventCode: currentEventCode,
      contacts: this.selectedContacts.map(x => ({
        name: x.name,
        email: x.email
      }))
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.inviteService.importContacts(model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.toastService.show('Succes', 'Contactele au fost importate cu succes.', 'success');
          this.getInvitedContacts();
          this.onStepSelected(this.steps.FILE);
          this.onTabSelected(this.tabs.INVITED);
        })
    );
  }

  onSendInvitesClick(resend: boolean = false) {
    const selectedContacts = this.contacts.filter(x => resend ? x.invitedDate : !x.invitedDate);
    if (selectedContacts.length == 0) {
      this.toastService.show('Eroare', `Nu exista invitatii de ${resend ? 'retrimis' : 'trimis'}.`, 'error');
      return;
    }

    const model = {
      eventCode: this.eventCode,
      canResend: resend
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.inviteService.inviteSend(model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.toastService.show('Succes', 'Invitatiile au fost trimise cu succes.', 'success');

          selectedContacts.forEach(x => {
            x.invitedDate = new Date();
          });

          this.resetFilter();
        })
    );
  }

  private cancelImport() {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.componentInstance.title = 'Opreste import';
    modalRef.componentInstance.description = 'Modificarile facute pana acum se vor pierde si nu pot fi recuperate.';
    modalRef.componentInstance.acceptLabel = 'Opreste';
    modalRef.componentInstance.acceptClass = 'btn-danger';
    modalRef.componentInstance.rejectLabel = 'Continua';

    this.subscriptions.push(
      modalRef.componentInstance.done
        .subscribe(result => {
          if (result === 'accept') {
            this.resetData();
            // this.router.navigate(['/eveniment', this.eventCode]);
          }
        })
    );
  }

  resetData() {
    this.onTabSelected(this.tabs.IMPORT);
    this.onStepSelected(this.steps.FILE);
    this.importedContacts = [];
    this.importedRawData = [];
    this.headers = [];
    this.formMapping.reset();
  }

  csvFileChangeListener($event): void {
    this.resetData();
    this.selectedImportSource = this.importSources.CSV;

    const selectedFile = $event.target.files[0];
    this.fileReset();

    if (['.csv'].some(x => selectedFile.name.toLowerCase().endsWith(x.toLowerCase())) == false) {
      this.toastService.show(
        'Eroare',
        'Selecteaza un fisier valid in format CSV.',
        'error'
      );
      return;
    }

    this.parseFile(selectedFile);
  }

  excelFileChangeListener($event): void {
    this.resetData();
    this.selectedImportSource = this.importSources.EXCEL;

    const selectedFile = $event.target.files[0];
    this.fileReset();

    if (['.xls', '.xlsx'].some(x => selectedFile.name.toLowerCase().endsWith(x.toLowerCase())) == false) {
      this.toastService.show(
        'Format fisier',
        'Selecteaza un fisier valid in format Excel.',
        'warning'
      );

      return;
    }

    this.parseFile(selectedFile);
  }

  private parseFile(selectedFile) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const workbook = XLSX.read(e.target.result, { type: 'array' });
      if (workbook.SheetNames.length > 0) {

        const rows = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]]
        );

        if (rows.length > 0) {
          this.headers.push({ name: 'Selecteaza coloana', value: null } as NameValue);

          for (const key in rows[0]) {
            if (rows[0].hasOwnProperty(key)) {
              this.headers.push({ name: key, value: key } as NameValue);
            }
          }

          //Try to match based on templates
          this.formMapping.patchValue({
            columnName: this.headers.find(x => x.value == 'Nume') ? 'Nume' : null,
            columnEmail: this.headers.find(x => x.value == 'Email') ? 'Email' : null
          });

          this.importedRawData = rows;
          this.onStepSelected(this.steps.MAPPING);
        }
      }

      if (this.headers.length <= 1) {
        this.toastService.show('Eroare', `Fisierul nu are header sau are un format incorect. `, 'error');
      }
    };

    reader.onerror = () => {
      this.toastService.show('Eroare', `Fisierul nu are header sau are un format incorect. `, 'error');
    };

    reader.readAsArrayBuffer(selectedFile);
  }

  onAddContactClick(template: TemplateRef<any>) {
    this.resetForm();
    this.editContactModal = this.modalService.open(template);
  }

  onContactSubmit() {
    if (!this.form.valid) {
      this.vs.triggerValidation(this.form);
      return;
    }

    if (this.editedItem) {
      this.updateContact();
    } else {
      this.addContact();
    }

    this.editContactModal.close();
  }

  private addContact() {
    const duplicateContact = this.contacts.find(x => x.email.toUpperCase() === this.form.value.email.toUpperCase());
    if (duplicateContact) {
      this.toastService.show(
        'Contact existent',
        'Contactul exista deja in lista.',
        'warning'
      );

      return;
    }

    const model = {
      ...this.form.value,
      eventCode: this.eventCode,
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.inviteService.createContact(model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe((response: any) => {
          this.contacts.unshift(response);
          this.resetFilter();
          this.toastService.show('Succes', 'Contactul a fost adaugat cu succes.', 'success');
        })
    );

  }

  updateContact() {
    const duplicateContact = this.contacts.find(x => x.email.toUpperCase() === this.form.value.email.toUpperCase() && x.id != this.editedItem.id);
    if (duplicateContact) {
      this.toastService.show(
        'Contact existent',
        'Contactul exista deja in lista.',
        'warning'
      );

      return;
    }

    const model = {
      ...this.form.value
    };

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.inviteService.updateContact(this.editedItem.id, model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.editedItem.name = this.form.value.name;
          this.editedItem.email = this.form.value.email;

          this.resetFilter();
          this.toastService.show('Succes', 'Contactul a fost modificat cu succes.', 'success');
        })
    );

  }

  private deleteContact(contactId: number) {
    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.inviteService.deleteContact(contactId)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          const contactIndex = this.contacts.findIndex(x => x.id == contactId);
          this.contacts.splice(contactIndex, 1);
          this.resetFilter();
          this.toastService.show('Succes', 'Contactul a fost sters cu succes.', 'success');
        })
    );
  }

  private resetForm() {
    this.editedItem = null;
    this.form.reset();
  }

  private getInvitedContacts() {
    this.ngxUiLoaderService.start();
    this.subscriptions.push(

      this.eventService.getPublicEventDetailsByCode(this.eventCode)
      .pipe(
        catchError(e => {
          this.toastService.checkErrorForStatus(e);
          return throwError(e);
        }),
        finalize(() => {
          this.ngxUiLoaderService.stop();
        })
      )
      .subscribe((event: any) => {
        localStorage.setItem(CURRENT_EVENT_KEY, this.eventCode.toString());

        if (event.status != 'Active') {
          this.router.navigate(['/error']);
          return;
        }

        if (this.user.id != event.userId) {
          this.router.navigate(['/forbidden']);
          return;
        }

        this.contacts = event.invites;
        this.contributions = event.contributions;
        this.filterContacts();
      })
    );
  }

  private resetFilter() {
    this.formFilter.reset({
      keyword: '',
      status: 'all'
    });

    this.filterContacts();
  }

  private fileReset() {
    this.fileImportInput.nativeElement.value = '';
    this.fileImportInputExcel.nativeElement.value = '';
  }

  private isValidItem(name: string, email: string): boolean {
    return !!name &&
      !!email &&
      NAME_REGEXP.test(name) &&
      EMAIL_REGEXP.test(email)
  }
}
